import type { CoberturaFx } from "@/routes/finance/datosApi"

/**
 * La logica de la pantalla de bancos y tipos de cambio.
 *
 * Dos cosas que aqui pueden mentir sin dar error, y que por eso viven en un
 * modulo con pruebas en vez de dentro del componente:
 *
 * 1. **Un tipo de cambio cargado al reves.** «Dolares por sol» en vez de «soles
 *    por dolar» no falla: da un consolidado catorce veces mas grande, que es
 *    plausible si nadie conoce las cifras de memoria.
 * 2. **Un tipo de cambio que se dejo de cargar.** Las consultas toman la ultima
 *    cotizacion ANTERIOR a la fecha pedida —que para eso esta, porque los
 *    domingos no cotizan—, asi que el consolidado de septiembre sigue saliendo,
 *    calculado a marzo, y nada avisa.
 */

// ---------------------------------------------------------------------------
// Pegar cotizaciones
// ---------------------------------------------------------------------------

export interface CotizacionLeida {
  fecha: string
  moneda: string
  tipo_cambio: number
}

export interface ErrorDeLinea {
  linea: number
  texto: string
  motivo: string
}

/**
 * El separador decimal es el ULTIMO signo.
 *
 * Es la regla que vale para «1.234,56» y para «1,234.56» sin preguntar el
 * locale, y es la misma que usa el lector de CSV del backend: dos reglas
 * distintas para el mismo dato es como se termina cargando 3752 donde iba
 * 3,752.
 */
export function aNumero(texto: string): number {
  const limpio = (texto || "").trim().replace(/\s/g, "").replace(/[^\d,.\-+]/g, "")
  if (!limpio) return NaN
  const coma = limpio.lastIndexOf(",")
  const punto = limpio.lastIndexOf(".")
  const normal = coma > punto ? limpio.replace(/\./g, "").replace(",", ".") : limpio.replace(/,/g, "")
  return Number(normal)
}

/** aaaa-mm-dd o dd/mm/aaaa. El peligroso es dd/mm contra mm/dd: se asume día primero. */
export function aFechaISO(texto: string): string | null {
  const t = (texto || "").trim()
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t)
  if (m) return armar(m[1], m[2], m[3])
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(t)
  if (m) return armar(m[3], m[2], m[1])
  return null
}

function armar(anio: string, mes: string, dia: string): string | null {
  const a = Number(anio)
  const me = Number(mes)
  const d = Number(dia)
  if (me < 1 || me > 12 || d < 1 || d > 31) return null
  const iso = `${a}-${String(me).padStart(2, "0")}-${String(d).padStart(2, "0")}`
  // El 31 de febrero pasa las comprobaciones de rango y no existe. `Date` lo
  // corre al 3 de marzo en silencio, que es justo lo que no se quiere.
  const control = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(control.getTime()) || control.getUTCDate() !== d) return null
  return iso
}

/**
 * Lee cotizaciones pegadas desde una hoja de calculo.
 *
 * Tres columnas —fecha, moneda, tipo de cambio— separadas por tabulador, punto
 * y coma o coma. El tabulador es el que produce copiar y pegar desde Excel, que
 * es como va a llegar esto de verdad.
 */
export function parsearCotizaciones(texto: string): {
  filas: CotizacionLeida[]
  errores: ErrorDeLinea[]
} {
  const filas: CotizacionLeida[] = []
  const errores: ErrorDeLinea[] = []

  const lineas = (texto || "").split(/\r?\n/)
  lineas.forEach((cruda, i) => {
    const linea = i + 1
    if (!cruda.trim()) return

    const partes = cruda.split(/\t|;|,(?=\s*[A-Za-z])/).map((p) => p.trim())
    const campos = partes.length >= 3 ? partes : cruda.trim().split(/\s+/)
    if (campos.length < 3) {
      errores.push({
        linea,
        texto: cruda.trim().slice(0, 60),
        motivo: "Hacen falta tres columnas: fecha, moneda y tipo de cambio.",
      })
      return
    }

    const fecha = aFechaISO(campos[0])
    if (!fecha) {
      errores.push({
        linea,
        texto: campos[0].slice(0, 40),
        motivo: "No es una fecha. Usa aaaa-mm-dd o dd/mm/aaaa.",
      })
      return
    }

    const moneda = campos[1].toUpperCase()
    if (!/^[A-Z]{3}$/.test(moneda)) {
      errores.push({ linea, texto: campos[1].slice(0, 40), motivo: "La moneda va en tres letras." })
      return
    }
    if (moneda === "USD") {
      // No es una cotización que falte: es una fila que no debe existir.
      errores.push({
        linea,
        texto: "USD",
        motivo: "El dólar es la base y no lleva fila: valdría 1 y ninguna consulta la espera.",
      })
      return
    }

    const valor = aNumero(campos[2])
    if (!Number.isFinite(valor) || valor <= 0) {
      errores.push({
        linea,
        texto: campos[2].slice(0, 40),
        motivo: "El tipo de cambio tiene que ser un número mayor que cero.",
      })
      return
    }

    filas.push({ fecha, moneda, tipo_cambio: valor })
  })

  return { filas, errores }
}

/**
 * Cotizaciones que parecen cargadas AL REVES.
 *
 * No se rechazan: es un aviso, no una validacion. Ninguna de las monedas que el
 * sistema opera vale menos de 0,5 por dolar —el sol ronda 3,7; el peso, 17; el
 * real, 5— asi que un valor por debajo de 0,5 es casi siempre «dolares por
 * unidad» en lugar de «unidades por dolar». Casi, no siempre: el dia que entre
 * una moneda que de verdad cotice por encima del dolar, rechazarla seria un
 * defecto y avisar sigue siendo correcto.
 */
export const SOSPECHOSO_POR_DEBAJO_DE = 0.5

export function cotizacionesSospechosas(filas: CotizacionLeida[]): CotizacionLeida[] {
  return filas.filter((f) => f.tipo_cambio < SOSPECHOSO_POR_DEBAJO_DE)
}

// ---------------------------------------------------------------------------
// Cobertura: hasta cuándo llega cada moneda
// ---------------------------------------------------------------------------

/** A partir de cuántos días sin cotizar se avisa. Un mes: más es un olvido. */
export const DIAS_PARA_AVISAR = 31

export interface CoberturaLeida extends CoberturaFx {
  diasSinCotizar: number
  vencida: boolean
}

/**
 * Cuánto hace que no se carga cada moneda.
 *
 * Es el dato que convierte un silencio en un aviso: sin esto, una moneda que
 * dejó de cargarse en marzo sigue produciendo consolidados de septiembre
 * calculados a marzo, y no hay ninguna cifra que se descuadre.
 */
export function leerCobertura(cobertura: CoberturaFx[], hoy: Date): CoberturaLeida[] {
  const hoyUTC = Date.UTC(hoy.getUTCFullYear(), hoy.getUTCMonth(), hoy.getUTCDate())
  return cobertura
    .map((c) => {
      const ultima = new Date(`${c.hasta}T00:00:00Z`).getTime()
      const dias = Number.isNaN(ultima)
        ? Number.POSITIVE_INFINITY
        : Math.floor((hoyUTC - ultima) / 86_400_000)
      return { ...c, diasSinCotizar: dias, vencida: dias > DIAS_PARA_AVISAR }
    })
    .sort((a, b) => b.diasSinCotizar - a.diasSinCotizar)
}
